/* eslint-disable-next-line react/no-danger */
/* eslint-disable-next-line react/no-unused-prop-types */

import React, { useRef, forwardRef, useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Case,
  CaseCreate,
  useCaseMutations,
  useCaseQuery,
  useCaseSectionsQuery,
  useCaseUsersQuery,
} from 'src/services/case';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { useNavigationItems } from 'src/hooks/useNavigationItems';
import ContainerBody from '../components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { ControlledInput } from '../components/ui/Input/Input';
import { ControlledSelect } from '../components/ui/Select/Select';
import Button, { ButtonToneProps } from '../components/ui/Button/Button';
import Chat from '../components/layouts/Chat/Chat';
import { OverlayContent, Header } from './CaseOverlayContent';
import { OverlayActions, useOverlay } from '../providers/OverlayProvider';
import styles from '../components/layouts/CreateSaleOrderOverlay/CreateSaleOrderOverlay.module.scss';
import Divider from '../components/ui/Divider/Divider';
import Attachments from '../components/layouts/OutboundOverlay/Attachments/Attachments';
import { ControlledTextarea } from '../components/ui/Textarea/Textarea';
import { RouteOverlay } from '../components/layouts/RouteOverlay/RouteOverlay';

const caseSchema = z.object({
  topic: z.string().min(1, 'Topic is required'),
  section: z.string().nullable(),
  description: z.string().optional().nullable(),
  due_date: z.string().optional().nullable(),
  resource_id: z.number().nullable(),
  assigned_user_id: z
    .array(z.union([z.string(), z.number()]))
    .transform(values => values.map(v => Number(v)))
    .optional()
    .nullable(),
  attachments: z
    .array(
      z.object({
        id: z.number().nullable().optional(),
        file: z.string().nullable().optional(),
        mimetype: z.string().optional(),
        name: z.string().optional(),
      }),
    )
    .nullable()
    .optional()
    .transform(attachments => {
      if (!attachments) return null;
      return attachments.map(attachment => ({
        mimetype: attachment.mimetype || '',
        name: attachment.name || '',
        file: attachment.file || '',
      }));
    }),
});

type CaseFormValues = z.infer<typeof caseSchema>;

type CaseFormProps = {
  initialData?: CaseCreate | Case;
  onSubmit: (data: CaseFormValues) => Promise<void>;
  mode: 'create' | 'edit';
};

function htmlToTextareaContent(html: string): string {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Replace <br> and <p> with newlines
  tempDiv.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
  tempDiv.querySelectorAll('p').forEach(p => {
    p.insertAdjacentText('afterend', '\n');
  });

  return tempDiv.textContent || tempDiv.innerText || '';
}

const transformCaseToFormValues = (caseData: Case): CaseFormValues => {
  return {
    topic: caseData.topic || '',
    section: caseData.section || null,
    description: (caseData.description && htmlToTextareaContent(caseData.description)) || null,
    due_date: caseData.due_date || null,
    resource_id: caseData.resource_id || null,
    assigned_user_id: caseData.assigned_user?.length
      ? (caseData.assigned_user.map(user => String(user.id)) as any[])
      : null,
    attachments:
      caseData.attachments?.map(attachment => ({
        id: attachment.id || null,
        file: attachment.file || '',
        mimetype: attachment.mimetype || '',
        name: attachment.name || '',
      })) || null,
  };
};

const CaseForm = forwardRef<HTMLFormElement, CaseFormProps>(({ initialData, onSubmit, mode }, ref) => {
  const { t } = useTranslation();
  const { data: sections } = useCaseSectionsQuery();
  const { data: users } = useCaseUsersQuery();
  const defaultValues = initialData
    ? transformCaseToFormValues(initialData as Case)
    : {
        topic: '',
        section: null,
        description: null,
        assigned_user_id: null,
        resource_id: 0,
        attachments: null,
      };

  const { handleSubmit, control, setValue } = useForm<CaseFormValues>({
    resolver: zodResolver(caseSchema),
    defaultValues,
  });

  return (
    <div className="flex flex-col w-full">
      <div className="flex md:flex-row gap-16 md:items-center items-start justify-between flex-col">
        {mode === 'create' && <h2 className="text-full typo-title-36">{t('case.create_case')}</h2>}
        {mode === 'edit' && <Header hideAction fetchedCase={initialData as Case} />}
      </div>
      <Divider className="my-24" />
      <form ref={ref} onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 gap-16">
        {mode === 'create' && (
          <div className="flex flex-col gap-8">
            <span className="typo-text-14--bold text-faded">{t('case.topic')}*</span>
            <ControlledInput control={control} name="topic" placeholder="" className="col-span-2" />
          </div>
        )}
        <div className="flex flex-col gap-8">
          <span className="typo-text-14--bold text-faded">{t('case.section')}*</span>
          <ControlledSelect
            control={control}
            name="section"
            isClearable
            containerClassName="col-span-2"
            options={
              // @ts-ignore
              sections?.map(section => ({
                value: section.key,
                label: section.label,
              })) || []
            }
            placeholder={t('general.select')}
          />
        </div>
        <div className="flex flex-col gap-8">
          <span className="typo-text-14--bold text-faded">{t('case.assigned_to')}</span>
          {!users ? (
            <ControlledSelect name="assigned_user_id" control={control} options={[]} isMultiSelect />
          ) : (
            <ControlledSelect
              name="assigned_user_id"
              control={control}
              options={users.map(user => ({
                value: String(user.id),
                label: user.name,
              }))}
              isMultiSelect
            />
          )}
        </div>
        <div className="flex flex-col gap-8">
          {mode === 'create' && (
            <div className="flex flex-col gap-8">
              <ControlledTextarea
                name="description"
                control={control}
                label={t('general.description')}
                placeholder=""
                className="col-span-2"
                addPaddingForIconRight
              />
            </div>
          )}
        </div>

        <div className={classNames(styles.info, `grid grid-cols-1 items-center gap-8`)}>
          <Attachments
            attachments={initialData?.attachments as any[]}
            edit={mode === 'edit' || mode === 'create'}
            updateAttachments={files => setValue('attachments', files)}
          />
        </div>
      </form>
    </div>
  );
});

type CaseOverlayProps = {
  mode: 'view' | 'create' | 'edit';
};

export function CaseOverlay({ mode: initialMode = 'view' }: CaseOverlayProps) {
  const { t } = useTranslation();
  const { onClose } = useOverlay();
  const { id: idString } = useParams();
  const id = idString ? Number.parseInt(idString, 10) : undefined;
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);

  useEffect(() => {
    if (initialMode !== 'create') {
      setMode('view');
    }
  }, [id]);

  const formRef = useRef<HTMLFormElement>(null);

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('cases');
  const chatAccess = findAccessRights('chatter');

  if (initialMode !== 'create' && (!id || Number.isNaN(id))) {
    throw new Error(`Invalid case ID: ${idString}`);
  }

  const { data: fetchedCase } = useCaseQuery(id as number, {
    enabled: mode === 'edit' || mode === 'view',
  });

  const { createMutation, updateMutation } = useCaseMutations();

  const handleSubmit = async (data: CaseFormValues) => {
    try {
      if (mode === 'create') {
        const response = await createMutation.mutateAsync({
          body: data,
        });
        toast.success(t('success.case_created'));
        onClose();
        setMode('view');
        navigate(`/cases/${response.id}`);
      } else if (mode === 'edit' && id) {
        await updateMutation.mutateAsync({
          params: {
            path: {
              case_id: id,
            },
          },
          body: {
            assigned_user_id: data.assigned_user_id,
            due_date: data.due_date,
            section: data.section,
            attachments: data.attachments,
          },
        });
        toast.success(t('success.case_updated'));
        setMode('view');
      }
    } catch (error) {
      const errorMessage = mode === 'create' ? 'errors.case_create_failed' : 'errors.case_update_failed';
      toast.error(t(errorMessage));
      console.error(`Failed to ${mode} case:`, error);
    }
  };

  const triggerSubmit = () => {
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const actions = useMemo(() => {
    if (mode === 'view' && accessRights?.read_write) {
      return (
        <div key="view-actions" className="flex gap-8">
          {/* Claim creation (Klärung / Schadensmeldung) now lives in the
              outbound overlay (shipped outbounds only), not on the case. */}
          <Button
            key="edit"
            disabled={fetchedCase?.is_locked}
            title={t('case.edit')}
            tone={ButtonToneProps.PRIMARY_BLUE}
            onClick={() => setMode('edit')}
          />
        </div>
      );
    }

    if (mode === 'edit') {
      return (
        <div key="edit-buttons" className="flex gap-2">
          <Button title={t('general.cancel')} onClick={() => setMode('view')} tone={ButtonToneProps.SECONDARY} />
        </div>
      );
    }

    return null;
  }, [mode, fetchedCase, t]);

  return (
    <RouteOverlay title={id ? t('case.title', { id }) : t('case.create_case')}>
      <OverlayActions>{actions}</OverlayActions>
      <ContainerBody>
        {mode === 'view' && fetchedCase ? (
          <>
            <OverlayContent fetchedCase={fetchedCase} allowEdit={accessRights?.read_write} />
            {fetchedCase.id && (chatAccess?.read || chatAccess?.read_write) && (
              <div className="mt-32">
                <Chat
                  withCreateEditMessage={chatAccess?.read_write}
                  model="project.task"
                  res_id={fetchedCase.id}
                  allowNote
                  allowCase={false}
                />
              </div>
            )}
          </>
        ) : (
          ((mode === 'edit' && fetchedCase) || mode === 'create') && (
            <div className="flex flex-col gap-16">
              <CaseForm
                ref={formRef}
                initialData={mode === 'edit' ? fetchedCase : undefined}
                onSubmit={handleSubmit}
                mode={mode}
              />
              <div className="flex w-full justify-end">
                <Button
                  title={mode === 'create' ? t('case.create_case') : t('general.save')}
                  tone={ButtonToneProps.PRIMARY_BLUE}
                  onClick={triggerSubmit}
                />
              </div>
            </div>
          )
        )}
      </ContainerBody>
    </RouteOverlay>
  );
}
